/**
 * Shared location modals: header location picker (geolocation-modal) and
 * site-suggestion modal when the user may be on the wrong region site.
 * Both use the same cookie (pxg_location) so choosing a location in the header
 * or dismissing the site suggestion prevents the other from re-prompting.
 */

const PXG_LOCATION_COOKIE = 'pxg_location';
const COOKIE_DURATION_DAYS = 180;

function getLocationCookie() {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${PXG_LOCATION_COOKIE}=`));
  return match ? match.split('=')[1] : null;
}

function setLocationCookie(country) {
  if (country == null || String(country).trim() === '') return;
  try {
    const date = new Date();
    date.setTime(date.getTime() + COOKIE_DURATION_DAYS * 24 * 60 * 60 * 1000);
    document.cookie = `${PXG_LOCATION_COOKIE}=${encodeURIComponent(country)};expires=${date.toUTCString()};path=/`;
  } catch (err) {
    console.error('Failed to set location cookie:', err);
  }
}

function getLocationFromUrl() {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('set-location');
}

// ---------------------------------------------------------------------------
// Geolocation modal (header-only: opened when user clicks location in header)
// ---------------------------------------------------------------------------

class GeolocationModal extends DialogModal {
  constructor() {
    super();
    this.dialog.addEventListener('close', this.close.bind(this));
  }

  close() {
    setLocationCookie(window.Shopify?.country);
  }
}

customElements.define('geolocation-modal', GeolocationModal);

// ---------------------------------------------------------------------------
// Site suggestion modal (auto-shown when detected country doesn’t match site)
// ---------------------------------------------------------------------------

const EMEA_COUNTRY_CODES = new Set([
  'GB', 'IE', // UK & Ireland
  'AL', 'AD', 'AT', 'BY', 'BE', 'BA', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IT', 'LV', 'LI', 'LT', 'LU', 'MK', 'MT', 'MD', 'MC', 'ME', 'NL', 'NO', 'PL', 'PT', 'RO', 'RU', 'SM', 'RS', 'SK', 'SI', 'ES', 'SE', 'CH', 'UA', 'VA', // Europe
  'AE', 'BH', 'IR', 'IQ', 'IL', 'JO', 'KW', 'LB', 'OM', 'PS', 'QA', 'SA', 'SY', 'TR', 'YE', // Middle East
  'DZ', 'AO', 'BJ', 'BW', 'BF', 'BI', 'CV', 'CM', 'CF', 'TD', 'KM', 'CG', 'CD', 'DJ', 'EG', 'GQ', 'ER', 'SZ', 'ET', 'GA', 'GM', 'GH', 'GN', 'GW', 'CI', 'KE', 'LS', 'LR', 'LY', 'MG', 'MW', 'ML', 'MR', 'MU', 'MA', 'MZ', 'NA', 'NE', 'NG', 'RW', 'ST', 'SN', 'SC', 'SL', 'SO', 'ZA', 'SS', 'SD', 'TZ', 'TG', 'TN', 'UG', 'ZM', 'ZW', // Africa
]);

class SiteSuggestionModal extends DialogModal {
  constructor() {
    super();
    this.dialog.addEventListener('close', this.handleDialogClose.bind(this));
  }

  connectedCallback() {
    this.checkAndShow();
  }

  async getDetectedCountry() {
    const root = window.Shopify?.routes?.root;
    if (!root) return null;
    try {
      const response = await fetch(
        root +
          'browsing_context_suggestions.json' +
          '?country[enabled]=true' +
          `&country[exclude]=${window.Shopify?.country ?? ''}` +
          '&language[enabled]=true' +
          `&language[exclude]=${window.Shopify?.language ?? ''}`,
      );
      if (!response.ok) {
        console.warn('Geolocation API returned', response.status, response.statusText);
        return null;
      }
      const data = await response.json();
      return data?.detected_values?.country?.handle || null;
    } catch (err) {
      console.error('Failed to get geolocation for site suggestion:', err);
      return null;
    }
  }

  getTargetSiteForCountry(detectedCountry) {
    if (detectedCountry == null || typeof detectedCountry !== 'string') return null;
    const code = String(detectedCountry).toUpperCase();
    return { country: EMEA_COUNTRY_CODES.has(code) ? 'GB' : code };
  }

  async checkAndShow() {
    try {
      const locationFromUrl = getLocationFromUrl();
      if (locationFromUrl) {
        setLocationCookie(locationFromUrl);
        return;
      }

      if (getLocationCookie()) return;

      const detectedCountry = await this.getDetectedCountry();
      const targetSite = this.getTargetSiteForCountry(detectedCountry);
      if (!targetSite) return;

      if (window.Shopify?.country === targetSite.country) {
        setLocationCookie(targetSite.country);
        return;
      }

      const links = this.querySelectorAll(`.site-suggestion-link[data-country="${targetSite.country}"]`);
      if (!links.length) return;

      links.forEach((link) => link.classList.remove('hidden'));
      this.show();
    } catch (err) {
      console.error('Site suggestion check failed:', err);
    }
  }

  handleDialogClose() {
    setLocationCookie(window.Shopify?.country);
  }
}

customElements.define('site-suggestion-modal', SiteSuggestionModal);
